import { api } from '../api/client';

import { API_ITEMS_URL } from '../utils';

/**
 * The two acts a RECORD OF RECORD supports instead of an ordinary edit: a
 * manager reopening a locked entry, and anyone restating one with a
 * correction that supersedes it.
 *
 * Both require a reason, and both leave it in the change log — "who reopened
 * this, when, and why" and "what does this record say now, and what did it
 * say before" are the two questions a locked record exists to answer.
 */

export async function unlockItem(itemId, { reason, minutes } = {}) {
	const { data } = await api.post(`${API_ITEMS_URL}/${itemId}/unlock`, {
		reason,
		...(minutes ? { minutes } : {}),
	});
	return data;
}

export async function correctItem(itemId, { reason, elements, notes } = {}) {
	const { data } = await api.post(`${API_ITEMS_URL}/${itemId}/correct`, {
		reason,
		...(elements ? { elements } : {}),
		...(notes !== undefined ? { notes } : {}),
	});
	return data;
}

/** A locked record refuses an ordinary save with this code. */
export const isLockedError = error => error?.response?.data?.code === 'item_locked';

/**
 * Triage an intake submission (`POST /api/items/{id}/intake-review`).
 *
 * `decision` is 'accept' or 'reject'. ACCEPT makes the submission a record
 * like any other — it joins the list, the counts, exports and the public
 * share reads, and the collection's automations see it once, as created.
 * REJECT DELETES it (the ordinary item delete, restorable from History back
 * into the queue); `reason` is kept with the deletion and is only sent on a
 * reject. Anyone who can write the item may do either.
 *
 * Accept → `{ ok, decision, item }`; reject → `{ ok, decision, _id }`. A 409
 * `not_pending` means somebody else already decided it.
 *
 * 'reopen' (KPR-482) sends a submission a public form's review RULE accepted
 * back to the queue, pending → `{ ok, decision, item }`; 409
 * `not_decided_by_rule` when it is not (or no longer) a rule's accept. A
 * reject of a rule's accept is the ordinary reject.
 */
export async function reviewIntakeItem(itemId, decision, { reason } = {}) {
	const body = { decision };
	// A reason rides a reject, and a reopen (KPR-482) a card's delete policy holds to one.
	if ((decision === 'reject' || decision === 'reopen') && typeof reason === 'string' && reason.trim()) body.reason = reason.trim();
	const { data } = await api.post(`${API_ITEMS_URL}/${itemId}/intake-review`, body);
	return data;
}

/** Somebody else decided this submission first. */
export const isNotPendingError = error => error?.response?.data?.code === 'not_pending';

/** A rule's accept that somebody already sent back (or a person's accept): KPR-482's reopen refusal. */
export const isNotDecidedByRuleError = error => error?.response?.data?.code === 'not_decided_by_rule';

/** Is this record an example a Discover package brought (and nobody has edited since)? */
export const isDemoItem = item => item?.provenance?.demo === true;

/** Is this item an intake submission nobody has accepted yet? */
export const isPendingIntake = item => item?.intake?.reviewState === 'pending';

/**
 * What a public form's visitor sent PRIVATELY with this submission (KPR-389,
 * `GET /api/items/{id}/intake-private`): the answers to the link's private
 * questions, kept apart from the record for the people who manage its
 * collection. `{ item_id, collection_id, elements: { <name>: value }, createdAt }`,
 * or null when there is nothing to show this reader — none was sent (404
 * `intake_private_none`), the reader does not manage the collection (403),
 * or the record is not theirs to read (404). Session only on the API: an API
 * key or assistant is refused whoever holds it.
 */
export async function getIntakePrivate(itemId) {
	try {
		const { data } = await api.get(`${API_ITEMS_URL}/${itemId}/intake-private`);
		return data && typeof data === 'object' ? data : null;
	} catch (error) {
		const status = error?.response?.status;
		if (status === 404 || status === 403) return null;
		throw error;
	}
}
